import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { useSolutionList } from '../../context/Solution/hooks/useSolutionList';
import Api from 'Api/Api';
import { url } from '@api-config';
import { getIsIngestRunning, getIsIngestStopping } from '../utilities/getIsIngestRunning';
import { IngestionLogCountsQueryKey } from './useIngestionLogCounts.query';
import { ingestionLogsKeys } from './useIngestionLogSummary.query';
import { useFetchLogDetails } from '../IngestionLayout/hooks/useFetchLogDetails';
import { useAppSelector } from '@redux/store';

interface IngestionData {
  startDate: string;
  status: number;
  ingestionId: string;
}

export const fullIngestionStatusKeys = {
  invalidationKey: ['fullIngestionStatus'],
  queryKey: (id?: number) => ['fullIngestionStatus', id],
};

export const useFullIngestionStatus = () => {
  const { solution } = useSolutionList();
  const id = useMemo(() => solution?.id, [solution]);

  const selectedLogId = useAppSelector((state) => state.ingestion.selectedLogId);

  const { fetchDetails } = useFetchLogDetails();

  const query = useQuery<IngestionData | null, Error>({
    queryKey: fullIngestionStatusKeys.queryKey(id),
    enabled: !!id && id > -1,
    queryFn: async () => {
      try {
        const data = await Api.get(`${url.events}/ingestion/solution/${id}`);
        return data;
      } catch (error: any) {
        if (error.status === 404) return null;
        throw error;
      }
    },
    refetchInterval: ({ state }) => {
      const status = state.data?.status;
      return status && (getIsIngestRunning(status) || getIsIngestStopping(status)) ? 5000 : false;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const status = query.data?.status;
  const running = getIsIngestRunning(status);
  const stopping = getIsIngestStopping(status);
  const idle = query.data === null || (!running && !stopping);
  const queryClient = useQueryClient();
  const previousDataRef = useRef<Pick<IngestionData, 'status'> | null>(null);

  // Fetch full ingestion completion ID
  useEffect(() => {
    if (!query.data) return;

    const previousStatus = previousDataRef?.current?.status;
    const wasRunningOrStopping =
      getIsIngestRunning(previousStatus) || getIsIngestStopping(previousStatus);
    const isNowIdle = !running && !stopping;
    const ingestionCompleted = wasRunningOrStopping && isNowIdle;

    if (ingestionCompleted) {
      queryClient.invalidateQueries({ queryKey: IngestionLogCountsQueryKey });
      queryClient.invalidateQueries({ queryKey: ingestionLogsKeys.all });

      // This call happens if you had the ingestion logs open, so the status is updated
      if (selectedLogId !== null && query.data?.ingestionId === selectedLogId) {
        fetchDetails(selectedLogId);
      }
    }
    // update the ref with the latest status
    previousDataRef.current = query.data;
  }, [query.data, running, stopping, queryClient, fetchDetails, selectedLogId]);

  return {
    ...query,
    status,
    isRunning: running,
    isStopping: stopping,
    isIdle: idle,
    ingestionId: query.data?.ingestionId,
    startDate: query.data?.startDate,
  };
};
